import { httpErrorInterceptor } from './core/interceptors/http-error.interceptor';
import {
    ApplicationConfig,
    inject,
    isDevMode,
    provideAppInitializer,
    provideEnvironmentInitializer,
    provideZonelessChangeDetection,
} from '@angular/core';
import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import { LuxonDateAdapter } from '@angular/material-luxon-adapter';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withComponentInputBinding, withInMemoryScrolling } from '@angular/router';
import { provideFuse } from '@fuse/fuse.provider';
import { TranslocoService, provideTransloco } from '@jsverse/transloco';
import { provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { appRoutes } from './app.routes';
//import { provideAuth } from './core/auth/auth.provider';
import { provideIcons } from 'app/core/icons/icons.provider';
import { MockApiService } from 'app/mock-api';
import { firstValueFrom } from 'rxjs';
import { TranslocoHttpLoader } from './core/transloco/transloco.http-loader';
import { ProvideAuthProvider, AUTH_ENVIRONMENT, providePermissionInitializer, provideOpenPayInitializer } from '@hta/auth';
import { environment } from 'environments/environment';
import { CatalogosSatService } from './modules/facturacion/services/catalogos-sat.service';
import { SatCatalogService } from './modules/facturacion/services/sat-catalog.service';
import { PermissionInvalidationUiBridge } from './core/auth/permission-invalidation-ui-bridge';
import {
    CONTRIBUYENTE_WIZARD_FEATURE_KEY,
    contribuyenteWizardReducer,
    ContribuyenteWizardEffects
} from '@hta/shared-ui';

export const appConfig: ApplicationConfig = {
    providers: [
        provideAnimations(),
        provideZonelessChangeDetection(),
        // Disable the native HTML Popover API for CDK overlays (mat-select, etc.)
        // Angular Material 21 uses the Popover API by default, which positions
        // incorrectly inside the Fuse compact layout (sticky nav + flex content).
        // Reverting to CDK's traditional position: fixed strategy fixes the issue.
        { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } },
        provideStore({ [CONTRIBUYENTE_WIZARD_FEATURE_KEY]: contribuyenteWizardReducer }),
        provideEffects([ContribuyenteWizardEffects]),
        ProvideAuthProvider(environment.amplify.Auth.userPoolId, environment.amplify.Auth.userPoolWebClientId, [httpErrorInterceptor]),
        providePermissionInitializer(),
        provideOpenPayInitializer(),
        {
            provide: AUTH_ENVIRONMENT,
            useValue: {
                htaBaseUrl: environment.htaBaseUrl ?? environment.baseUrl,
                baseUrl: environment.baseUrl,
                erpBaseUrl: environment.erpBaseUrl,
                apiUrl: environment.apiUrl,
                apiKey: environment.apiKey,
                openPay: environment.openPay,
                modules: (environment as { modules?: Record<string, string> }).modules,
            },
        },
        provideRouter(
            appRoutes,
            withComponentInputBinding(),
            withInMemoryScrolling({ scrollPositionRestoration: 'enabled' })
        ),

        // Material Date Adapter
        {
            provide: DateAdapter,
            useClass: LuxonDateAdapter,
        },
        {
            provide: MAT_DATE_FORMATS,
            useValue: {
                parse: {
                    dateInput: 'dd / MM / yyyy',
                },
                display: {
                    dateInput: 'dd / MM / yyyy',
                    monthYearLabel: 'LLL yyyy',
                    dateA11yLabel: 'dd / MM / yyyy',
                    monthYearA11yLabel: 'LLLL yyyy',
                },
            },
        },

        // Transloco Config
        provideTransloco({
            config: {
                availableLangs: [
                    {
                        id: 'en',
                        label: 'English',
                    },
                    {
                        id: 'tr',
                        label: 'Turkish',
                    },
                ],
                defaultLang: 'en',
                fallbackLang: 'en',
                reRenderOnLangChange: true,
                prodMode: !isDevMode(),
            },
            loader: TranslocoHttpLoader,
        }),
        provideAppInitializer(() => {
            const translocoService = inject(TranslocoService);
            const defaultLang = translocoService.getDefaultLang();
            translocoService.setActiveLang(defaultLang);

            return firstValueFrom(translocoService.load(defaultLang));
        }),

        // Warm-up del catálogo SAT de unidades (fire-and-forget): la primera vez que
        // el usuario abra un autocomplete ya estará en memoria/localStorage.
        // Fallos se ignoran: el autocomplete reintenta bajo demanda con cache SWR.
        provideAppInitializer(() => {
            inject(SatCatalogService).ensureLoaded().catch(() => {
                // Si IndexedDB o la red fallan, el autocomplete reintenta vía
                // CatalogosSatService.getUnidadesParaAutocomplete() en su ngOnInit.
                inject(CatalogosSatService).getUnidadesParaAutocomplete().catch(() => {});
            });
        }),

        // Activa el listener que muestra un toast cuando otra pestaña del mismo origin
        // notifica cambios de permisos via BroadcastChannel.
        provideEnvironmentInitializer(() => inject(PermissionInvalidationUiBridge)),

       
        
        // Fuse
        provideIcons(),
        provideFuse({
            mockApi: {
                delay: 0,
                service: MockApiService,
            },
            fuse: {
                layout: 'futuristic',
                scheme: 'light',
                screens: {
                    sm: '600px',
                    md: '960px',
                    lg: '1280px',
                    xl: '1440px',
                },
                theme: 'theme-default',
                themes: [
                    {
                        id: 'theme-default',
                        name: 'Default',
                    },
                    {
                        id: 'theme-brand',
                        name: 'Brand',
                    },
                    {
                        id: 'theme-teal',
                        name: 'Teal',
                    },
                    {
                        id: 'theme-rose',
                        name: 'Rose',
                    },
                    {
                        id: 'theme-purple',
                        name: 'Purple',
                    },
                    {
                        id: 'theme-amber',
                        name: 'Amber',
                    },
                ],
            },
        }),
    ],
};


